<script setup lang="ts">
import { IconX } from '@tabler/icons-vue'
import { computed, nextTick, onMounted, ref } from 'vue'

import '@tix/ds/styles/portico/component/modal.css'

import BaseButton from '../../atoms/BaseButton.vue'
import BaseIcon from '../../atoms/BaseIcon.vue'

import type { ModalAction, ModalSize } from './base-modal'

type Props = {
  name: string
  title?: string
  // TODO Figure out if this size should affect both width on desktop and height on mobile or they should be separate.
  // The size of the modal. Affects width on desktop and height on mobile.
  size?: ModalSize
  // Allow closing the modal by clicking the close button in the header.
  closeable?: boolean
  // Allow dismissing via backdrop click (desktop) and swipe-to-dismiss (mobile).
  dismissible?: boolean
  describedBy?: string
  actions?: ModalAction[]
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  closeable: true,
  dismissible: false,
})
const titleClass = computed(() => (props.title ? 'has-title' : 'no-title'))

const emit = defineEmits<{
  close: []
}>()

const dialogRef = ref<HTMLDialogElement | null>(null)
const modalWindowRef = ref<HTMLElement | null>(null)
// Toggled on nextTick after showModal() to trigger the CSS enter transition (opacity/transform).
const isOpen = ref(false)
const isClosing = ref(false)

onMounted(() => {
  // Template ref — guaranteed to be set before onMounted
  dialogRef.value!.showModal()
  nextTick(() => {
    isOpen.value = true
  })
})

function onTransitionEnd(el: HTMLElement, callback: () => void): void {
  let done = false
  function onEnd(e?: TransitionEvent): void {
    /* v8 ignore next: only fires via real CSS transitions, not in test env -- @preserve */
    if (e && e.target !== el) return
    /* v8 ignore next: prevents double callback if both transitionend and fallback fire -- @preserve */
    if (done) return
    done = true
    clearTimeout(fallback)
    el.removeEventListener('transitionend', onEnd)
    callback()
  }
  el.addEventListener('transitionend', onEnd)
  // Safety fallback in case transitionend doesn't fire (reduced-motion, detached element, etc.)
  const fallback = setTimeout(onEnd, 250)
}

function animateClose(): void {
  /* v8 ignore next: double-close race guard; each test only triggers one close path -- @preserve */
  if (isClosing.value) return
  isClosing.value = true
  isOpen.value = false

  const el = modalWindowRef.value
  if (!el) return emit('close')

  onTransitionEnd(el, () => {
    el.style.transform = ''
    emit('close')
  })
}

const pendingAction = ref<Record<string, true>>({})

async function handleAction(action: ModalAction): Promise<void> {
  pendingAction.value[action.label] = true
  try {
    await action.onAction?.(animateClose)
  } finally {
    delete pendingAction.value[action.label]
  }
}

// Close: close button + Escape key
function handleClose(): void {
  if (!props.closeable) return
  animateClose()
}

// Dismiss: backdrop click (desktop) + swipe-to-dismiss (mobile)
function handleDismiss(event: MouseEvent): void {
  if (props.dismissible && event.target === event.currentTarget) {
    animateClose()
  }
}

// --- Drag-to-dismiss ---
//
// Style mutations (transform, transition) are applied directly to the DOM rather than
// through reactive bindings. Pointer events fire ~60 times/sec during a drag; routing
// each frame through Vue's reactivity (ref → proxy trap → re-render) adds overhead for
// a purely visual transform.

const DISMISS_THRESHOLD = 100
const isDragging = ref(false)
let dragStartY = 0
let dragDeltaY = 0

function onDragStart(e: PointerEvent): void {
  /* v8 ignore next: drag handle only renders when dismissible; isClosing is a race guard -- @preserve */
  if (!props.dismissible || isClosing.value) return
  /* v8 ignore next: only respond to touch input; mouse/pen users have backdrop-click -- @preserve */
  if (e.pointerType !== 'touch') return
  isDragging.value = true
  dragStartY = e.clientY
  dragDeltaY = 0
  // Capture guarantees all subsequent pointer events target this element,
  // even if the finger moves outside the header bounds mid-drag.
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  // Disable CSS transition so the modal tracks the finger position exactly
  modalWindowRef.value!.style.transition = 'none'
}

function onDragMove(e: PointerEvent): void {
  /* v8 ignore next: pointermove only fires during an active drag sequence -- @preserve */
  if (!isDragging.value) return
  dragDeltaY = Math.max(0, e.clientY - dragStartY)
  modalWindowRef.value!.style.transform = `translateY(${dragDeltaY}px)`
}

function snapBack(): void {
  /* v8 ignore next: pointercancel only fires during an active drag sequence -- @preserve */
  if (!isDragging.value) return
  isDragging.value = false
  const el = modalWindowRef.value!
  el.style.transition = ''
  el.style.transform = ''
}

function onDragEnd(): void {
  /* v8 ignore next: pointerup only fires during an active drag sequence -- @preserve */
  if (!isDragging.value) return
  isDragging.value = false

  const el = modalWindowRef.value!

  // Re-enable CSS transition for the snap-back or dismiss animation
  el.style.transition = ''

  if (dragDeltaY > DISMISS_THRESHOLD) {
    el.style.transform = 'translateY(100%)'
    animateClose()
  } else {
    el.style.transform = ''
  }
}
</script>

<template>
  <dialog
    ref="dialogRef"
    :aria-describedby="describedBy"
    :aria-label="title ? undefined : name"
    :aria-labelledby="title ? `${name}-modal-title` : undefined"
    :class="['base-modal', size, { open: isOpen }]"
    :data-name="name"
    @cancel.prevent="handleClose"
    @click="handleDismiss"
  >
    <div ref="modalWindowRef" class="modal-window">
      <header
        :class="['modal-header', titleClass, { dismissible: dismissible }]"
        @pointerdown="onDragStart"
        @pointermove="onDragMove"
        @pointerup="onDragEnd"
        @pointercancel="snapBack"
      >
        <div v-if="dismissible" class="drag-handle">
          <span aria-hidden="true" />
        </div>
        <h2 v-if="title" :id="`${name}-modal-title`" class="modal-title">{{ title }}</h2>
        <button v-if="closeable" aria-label="Close" class="close-button" type="button" @click="handleClose">
          <BaseIcon class="close-icon" :icon="IconX" :stroke="1.75" />
        </button>
      </header>

      <div :class="['modal-body', titleClass]" tabindex="0">
        <slot />
      </div>

      <div v-if="actions?.length" class="modal-footer">
        <BaseButton
          v-for="action in actions"
          :key="action.label"
          :size="action.size"
          :hierarchy="action.hierarchy ?? 'primary'"
          :intent="action.intent ?? 'confirm'"
          :loading="pendingAction[action.label]"
          @click="handleAction(action)"
        >
          {{ action.label }}
        </BaseButton>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
.base-modal {
  --_transition: 200ms ease;

  display: flex;
  flex-direction: column;
  border: none;
  padding: 0;
  max-height: 85vh;
  overflow: visible;
  background: transparent;

  @media (--mobile-experience) {
    min-width: 100%;
    top: auto;
    bottom: 0;
    margin: 0;
  }

  &::backdrop {
    background: rgba(0, 0, 0, 0.5);
    transition: opacity var(--_transition);
  }

  &.sm {
    width: 30rem;
    max-width: 100%;

    @media (--mobile-experience) {
      width: 100%;
      max-height: 40dvh;
    }
  }

  &.md {
    width: 48rem;
    max-width: 100%;

    @media (--mobile-experience) {
      width: 100%;
      max-height: 65dvh;
    }
  }

  &.lg {
    width: 64rem;
    max-width: 100%;

    @media (--mobile-experience) {
      width: 100%;
      max-height: 85dvh;
    }
  }

  /* xl stays a max-width ceiling so extra-large content can still shrink. */
  &.xl {
    max-height: 95dvh;
    max-width: 64rem;

    .modal-window {
      max-height: 95dvh;
    }
  }

  .modal-window {
    display: flex;
    flex-direction: column;
    max-height: 85vh;
    border-radius: var(--radius-large);
    border: 1px solid var(--modal-border);
    box-shadow: var(--shadow-lg);
    overflow: hidden;
    background: var(--modal-content-bg);

    opacity: 0;
    transition: opacity var(--_transition);

    @media (--mobile-experience) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      transform: translateY(100%);
      transition:
        opacity var(--_transition),
        transform var(--_transition);
    }

    .base-modal.open & {
      opacity: 1;

      @media (--mobile-experience) {
        transform: translateY(0);
      }
    }

    .modal-header {
      display: grid;
      grid-template: 'title close' / 1fr auto;
      align-items: center;
      gap: 0 var(--space-1);
      padding: var(--space-1) var(--space-1-5);
      border-bottom: 1px solid var(--modal-border);
      flex-shrink: 0;
      background: var(--modal-header-bg);

      @media (--mobile-experience) {
        border: none;
        padding-bottom: 0;
        background: transparent;
      }

      .drag-handle {
        grid-area: drag;
        display: none;

        span {
          width: 2rem;
          height: 4px;
          border-radius: 2px;
          background: var(--text-tertiary);
        }
      }

      .modal-title {
        grid-area: title;
        margin: 0;
        color: var(--modal-header-text);
      }

      .close-button {
        display: flex;
        background: none;
        border: none;
        border-radius: var(--radius-medium);
        padding: var(--space-0-25);
        color: var(--modal-header-text);
        grid-area: close;
        justify-self: end;
        transition: opacity 0.15s ease-in-out;

        .close-icon {
          font-size: 13px;
        }

        &:hover {
          opacity: 0.6;
        }
      }

      &.no-title {
        border-bottom: none;
        background: transparent;
        padding-bottom: var(--space-0-5);
      }

      &.dismissible {
        @media (--mobile-experience) {
          grid-template:
            'drag drag' auto
            'title title' auto
            / 1fr auto;
          padding-top: var(--space-0-75);
          touch-action: none;

          cursor: grab;

          .drag-handle {
            display: flex;
            justify-content: center;
            padding: var(--space-0-5);
          }

          .close-button {
            grid-area: 1 / 2;
            align-self: center;
          }

          .modal-title {
            margin-top: var(--space-0-5);
          }

          &.no-title {
            grid-template: 'drag drag' / 1fr auto;
          }
        }
      }
    }

    .modal-body {
      flex: 1;
      overflow-y: auto;
      padding: var(--space-1-5);
      outline: none;
      color: var(--text-primary);

      &.no-title {
        padding-top: 0;
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--space-0-75);
      padding: var(--space-1) var(--space-1-5);
      border-top: 1px solid var(--modal-border);
      flex-shrink: 0;
      background: var(--modal-footer-bg);

      @media (--mobile-experience) {
        flex-direction: column-reverse;
        padding: var(--space-1-5);
      }
    }
  }
}
</style>
